import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FormSubmissionImport } from "@/components/FormSubmissionImport";
import { FormValidationCard } from "@/components/FormValidationCard";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { FormSubmission } from "@/types/formSubmissions";
import { AlertCircle, FileCheck, Upload } from "lucide-react";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";

export default function FormValidation() {
  const { isAdmin, isEditor } = useUserRole();
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");

  useEffect(() => {
    fetchSubmissions();
  }, [activeTab]);

  const fetchSubmissions = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('form_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (activeTab === 'pending') {
        query = query.eq('validation_status', 'pending');
      } else if (activeTab === 'approved') {
        query = query.eq('validation_status', 'approved');
      } else if (activeTab === 'rejected') {
        query = query.eq('validation_status', 'rejected');
      }

      const { data, error } = await query;

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAdmin && !isEditor) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <AppSidebar onClearData={() => {}} />
          <main className="flex-1 flex items-center justify-center p-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Você não tem permissão para acessar esta página.
              </AlertDescription>
            </Alert>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar onClearData={() => {}} />
        <main className="flex-1 overflow-auto p-6 space-y-6">
          <div>
        <h1 className="text-3xl font-bold tracking-tight">Validação de Formulários</h1>
        <p className="text-muted-foreground mt-2">
          Importe e valide formulários e logos do Google Forms
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="import" className="gap-2">
            <Upload className="h-4 w-4" />
            Importar
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            <FileCheck className="h-4 w-4" />
            Pendentes
            {submissions.length > 0 && activeTab === 'pending' && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
                {submissions.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved">Aprovados</TabsTrigger>
          <TabsTrigger value="rejected">Rejeitados</TabsTrigger>
          <TabsTrigger value="all">Todos</TabsTrigger>
        </TabsList>

        <TabsContent value="import" className="space-y-6">
          <FormSubmissionImport />
        </TabsContent>

        <TabsContent value="pending" className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : submissions.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Nenhum registro pendente</CardTitle>
                <CardDescription>
                  Não há formulários aguardando validação no momento.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {submissions.map((submission) => (
                <FormValidationCard
                  key={submission.id}
                  submission={submission}
                  onUpdate={fetchSubmissions}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : submissions.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Nenhum registro aprovado</CardTitle>
                <CardDescription>
                  Não há formulários aprovados ainda.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {submissions.map((submission) => (
                <FormValidationCard
                  key={submission.id}
                  submission={submission}
                  onUpdate={fetchSubmissions}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rejected" className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : submissions.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Nenhum registro rejeitado</CardTitle>
                <CardDescription>
                  Não há formulários rejeitados.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {submissions.map((submission) => (
                <FormValidationCard
                  key={submission.id}
                  submission={submission}
                  onUpdate={fetchSubmissions}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : submissions.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Nenhum registro</CardTitle>
                <CardDescription>
                  Não há formulários importados. Use a aba "Importar" para adicionar dados.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {submissions.map((submission) => (
                <FormValidationCard
                  key={submission.id}
                  submission={submission}
                  onUpdate={fetchSubmissions}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
          </main>
        </div>
      </SidebarProvider>
    );
  }
